import { Box, ListItem, Tooltip } from '@mui/material';
import * as React from 'react';
import { HiLockClosed } from 'react-icons/hi';
import { NavLink } from 'react-router-dom';
import { usePathname } from '~/framework/usePathname';

export function SidebarItem({
  href,
  target,
  icon: Icon,
  rightAdornment: RightAdornment,
  title,
  deemphasized,
  sx,
  isCollapsed = false,
  ...rest
}: {
  href: string;
  target?: string;
  title: string;
  icon?: React.ReactNode;
  rightAdornment?: React.ReactNode;
  deemphasized?: boolean;
  sx?: any;
  isCollapsed?: boolean;
}) {
  const pathname = usePathname();
  const itemIsActive = pathname?.startsWith(href);

  const sidebarItem = (
    <ListItem
      disableGutters
      style={{color: '#6A7181'}}
      sx={{
        display: 'flex',
        py: 0,
        px: isCollapsed ? 0.5 : 1.5,
        mb: 0,
        transition: 'padding 0.3s ease-in-out',
        ...sx,
      }}
      {...rest}
    >
      <Box
        component={NavLink}
        to={href}
        target={target}
        color="info"
        sx={{
          borderRadius: 1,
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          letterSpacing: 0,
          py: 0.5,
          px: isCollapsed ? 0.75 : 1.25,
          textDecoration: 'none',
          textTransform: 'none',
          display: 'flex',
          gap: isCollapsed ? 0 : 1.5,
          width: '100%',
          color: itemIsActive ? 'gray.800' : 'gray.500',
          transition: 'all 0.3s ease-in-out',
          minWidth: isCollapsed ? 'auto' : 'initial',
          ...(deemphasized && {
            color: 'gray.500',
            pointerEvents: 'none',
          }),
          '&::before': {
            content: '\'\'',
            position: 'absolute',
            top: 3,
            bottom: 3,
            left: isCollapsed ? -3 : -3,
            width: 6,
            backgroundColor: itemIsActive ? 'gray.700' : 'transparent',
            borderRadius: 2,
          },
          '&:hover': {
            bgcolor: itemIsActive ? 'hsl(213deg 94% 18% / 5%)' : 'hsl(210deg 88% 21% / 3%)',
            cursor: 'pointer',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            flexShrink: 0,
          }}
        >
          {Icon && Icon}
        </Box>
        {!isCollapsed && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              textOverflow: 'ellipsis',
              fontSize: 'sm',
              fontWeight: itemIsActive ? '600' : 'medium',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {title}
            {RightAdornment && RightAdornment}
          </Box>
        )}
        {deemphasized && !isCollapsed && (
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'gray.300',
              height: 12,
            }}
          >
            <HiLockClosed size={12} />
          </Box>
        )}
      </Box>
    </ListItem>
  );

  if (isCollapsed) {
    return (
      <Tooltip title={title} placement="right" arrow>
        {sidebarItem}
      </Tooltip>
    );
  }

  return sidebarItem;
}
